import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleMinus, faCirclePlus } from '@fortawesome/pro-light-svg-icons';
import clsx from 'clsx';
import styles from './EditableCounterField.module.scss';

export interface EditableCounterFieldProps {
    value?: number;
    minLimit?: number;
    maxLimit?: number;
    step?: number;
    readOnly?: boolean;
    disabled?: boolean;
    className?: string;
    hasBorder?: boolean;
    placeholder?: string;
    onChange: (value: number) => void;
}

export const EditableCounterField: React.FC<EditableCounterFieldProps> = ({ readOnly = false, step = 1, ...props }) => {
    const [inputValue, setInputValue] = useState<string>(props.value !== undefined ? String(props.value) : '');
    const [isFocused, setIsFocused] = useState(false);

    const roundFloat = useCallback((n: number, decimals = 10): number => {
        const factor = 10 ** decimals;
        return Math.round(n * factor) / factor;
    }, []);

    const displayValue = props.value !== undefined ? props.value : 0;
    const hasValue = props.value !== undefined;
    const isPlaceholderVisible = (!isFocused && !hasValue) || (isFocused && inputValue === '');

    useEffect(() => {
        if (!isFocused && props.value !== undefined) {
            setInputValue(String(props.value));
        } else if (!isFocused && props.value === undefined) {
            setInputValue('');
        }
    }, [props.value, isFocused]);

    const clampValue = useCallback(
        (n: number): number => {
            let result = n;
            if (props.minLimit !== undefined && result < props.minLimit) {
                result = props.minLimit;
            }
            if (props.maxLimit !== undefined && result > props.maxLimit) {
                result = props.maxLimit;
            }
            return result;
        },
        [props.minLimit, props.maxLimit]
    );

    const commitInputValue = useCallback(() => {
        const normalized = inputValue.replace(',', '.');
        const parsed = parseFloat(normalized);
        if (
            inputValue === '' ||
            inputValue === '.' ||
            inputValue === ',' ||
            inputValue === '-' ||
            inputValue === '-.' ||
            inputValue === '-,' ||
            Number.isNaN(parsed)
        ) {
            const fallback = props.minLimit ?? 0;
            props.onChange(clampValue(fallback));
            setInputValue(String(props.minLimit ?? 0));
            return;
        }
        const clamped = clampValue(parsed);
        props.onChange(clamped);
        setInputValue(String(clamped));
    }, [inputValue, props.onChange, props.minLimit, clampValue]);

    const changeHandler = (action: 'increment' | 'decrement') => {
        if (props.value === undefined) return;

        if (action === 'increment') {
            if (props.maxLimit !== undefined && props.value >= props.maxLimit) return;
            const next = roundFloat(props.value + step);
            props.onChange(next);
        } else {
            const newValue = roundFloat(props.value - step);
            props.onChange(clampValue(newValue));
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (raw === '' || raw === '-' || raw === '.' || raw === ',' || raw === '-.' || raw === '-,') {
            setInputValue(raw);
            return;
        }
        if (/^-?\d*[.,]?\d*$/.test(raw)) {
            setInputValue(raw);
            const normalized = raw.replace(',', '.');
            const parsed = parseFloat(normalized);
            if (!Number.isNaN(parsed)) {
                const clamped = clampValue(parsed);
                props.onChange(clamped);
                if (clamped !== parsed) {
                    setInputValue(String(clamped));
                }
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            commitInputValue();
        }
    };

    return (
        <div
            className={clsx(styles.container, props.className, props.hasBorder && styles.border, {
                [styles.disabled]: props.disabled
            })}
        >
            <button
                type="button"
                id="editable-counter-field-decrement-button"
                className={clsx(styles.button, (readOnly || isPlaceholderVisible) && styles.hidden)}
                onClick={() => changeHandler('decrement')}
                disabled={props.disabled}
            >
                <FontAwesomeIcon icon={faCircleMinus} />
            </button>
            <input
                type="text"
                inputMode="decimal"
                className={styles.input}
                value={isFocused ? inputValue : hasValue ? String(displayValue) : ''}
                placeholder={props.placeholder}
                onChange={handleInputChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    setIsFocused(false);
                    commitInputValue();
                }}
                onKeyDown={handleKeyDown}
                readOnly={readOnly}
                disabled={props.disabled}
                aria-label="Valor del contador"
            />
            <button
                type="button"
                id="editable-counter-field-increment-button"
                className={clsx(styles.button, (readOnly || isPlaceholderVisible) && styles.hidden)}
                onClick={() => changeHandler('increment')}
                disabled={props.disabled}
            >
                <FontAwesomeIcon icon={faCirclePlus} />
            </button>
        </div>
    );
};

export default EditableCounterField;
